import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import type {
  CompanyFormValues,
  DefaultsFormValues,
  TaxFormValues,
} from "@/features/settings/schema";
import type { Company, TaxRate } from "@/features/settings/types";
import { emptyCompanyForm } from "@/features/settings/schema";

const text = (value: string | undefined) => value?.trim() || null;

function money(value: unknown) {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

function normalizeCompany(row: Company): Company {
  return {
    ...row,
    default_gst_slab: money(row.default_gst_slab) || 18,
    fiscal_year_start_month: row.fiscal_year_start_month || 4,
    so_prefix: row.so_prefix || "SO",
    credit_note_prefix: row.credit_note_prefix || "CN",
    plan_prefix: row.plan_prefix || "PLAN",
    dispatch_prefix: row.dispatch_prefix || "DN",
    mill_kerf_mm: money(row.mill_kerf_mm) || 3,
    mill_edge_trim_mm: money(row.mill_edge_trim_mm) || 10,
    mill_weld_allowance_mm: money(row.mill_weld_allowance_mm) || 1,
    mill_tube_weight_factor: money(row.mill_tube_weight_factor) || 0.0246615,
    mill_ctl_blade_mm: money(row.mill_ctl_blade_mm) || 3,
    mill_ctl_yield_margin_pct: money(row.mill_ctl_yield_margin_pct) || 5,
    mill_skinpass_weight_factor: money(row.mill_skinpass_weight_factor) || 0.999,
    mill_inbound_default_process_type_id: row.mill_inbound_default_process_type_id ?? null,
  };
}

export function companyToForm(c: Company): CompanyFormValues {
  return {
    name: c.name,
    gstin: c.gstin ?? "",
    pan_no: c.pan_no ?? "",
    cin_no: c.cin_no ?? "",
    phone: c.phone ?? "",
    email: c.email ?? "",
    website: c.website ?? "",
    addr1: c.addr1 ?? c.address ?? "",
    addr2: c.addr2 ?? "",
    city: c.city ?? "",
    state: c.state ?? "",
    country: c.country || "India",
    pincode: c.pincode ?? "",
    client_prefix: c.client_prefix || "CL",
    vendor_prefix: c.vendor_prefix || "VN",
    po_prefix: c.po_prefix || "PO",
    invoice_prefix: c.invoice_prefix || "INV",
    quotation_prefix: c.quotation_prefix || "QT",
    so_prefix: c.so_prefix || "SO",
    credit_note_prefix: c.credit_note_prefix || "CN",
    plan_prefix: c.plan_prefix || "PLAN",
    dispatch_prefix: c.dispatch_prefix || "DN",
    bank_name: c.bank_name ?? "",
    bank_account: c.bank_account ?? "",
    bank_ifsc: c.bank_ifsc ?? "",
  };
}

export function companyToDefaultsForm(c: Company): DefaultsFormValues {
  return {
    default_payment_terms: c.default_payment_terms ?? "",
    default_gst_slab: money(c.default_gst_slab) || 18,
    fiscal_year_start_month: c.fiscal_year_start_month || 4,
    default_location_id: c.default_location_id ?? "",
    mill_kerf_mm: money(c.mill_kerf_mm) || 3,
    mill_edge_trim_mm: money(c.mill_edge_trim_mm) || 10,
    mill_weld_allowance_mm: money(c.mill_weld_allowance_mm) || 1,
    mill_tube_weight_factor: money(c.mill_tube_weight_factor) || 0.0246615,
    mill_ctl_blade_mm: money(c.mill_ctl_blade_mm) || 3,
    mill_ctl_yield_margin_pct: money(c.mill_ctl_yield_margin_pct) || 5,
    mill_skinpass_weight_factor: money(c.mill_skinpass_weight_factor) || 0.999,
    mill_inbound_default_process_type_id: c.mill_inbound_default_process_type_id ?? "",
  };
}

function prefixPayload(values: CompanyFormValues) {
  return {
    client_prefix: values.client_prefix.trim().toUpperCase(),
    vendor_prefix: values.vendor_prefix.trim().toUpperCase(),
    po_prefix: values.po_prefix.trim().toUpperCase(),
    invoice_prefix: values.invoice_prefix.trim().toUpperCase(),
    quotation_prefix: values.quotation_prefix.trim().toUpperCase(),
    so_prefix: values.so_prefix.trim().toUpperCase(),
    credit_note_prefix: values.credit_note_prefix.trim().toUpperCase(),
    plan_prefix: values.plan_prefix.trim().toUpperCase(),
    dispatch_prefix: values.dispatch_prefix.trim().toUpperCase(),
  };
}

export function useCompany() {
  return useQuery({
    queryKey: ["company"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<Company | null> => {
      const { data, error } = await supabase
        .from("company")
        .select("*")
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data ? normalizeCompany(data as Company) : null;
    },
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, values }: { id: string; values: CompanyFormValues }) => {
      const payload = {
        name: values.name.trim(),
        gstin: text(values.gstin)?.toUpperCase() ?? null,
        pan_no: text(values.pan_no)?.toUpperCase() ?? null,
        cin_no: text(values.cin_no)?.toUpperCase() ?? null,
        phone: text(values.phone),
        email: text(values.email),
        website: text(values.website),
        addr1: text(values.addr1),
        addr2: text(values.addr2),
        city: text(values.city),
        state: text(values.state),
        country: values.country.trim() || "India",
        pincode: text(values.pincode),
        address: text(values.addr1),
        ...prefixPayload(values),
        bank_name: text(values.bank_name),
        bank_account: text(values.bank_account),
        bank_ifsc: text(values.bank_ifsc)?.toUpperCase() ?? null,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };
      const { data, error } = await supabase
        .from("company")
        .update(payload)
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return normalizeCompany(data as Company);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["company"] });
      toast.success("Company profile saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to save company"),
  });
}

export function useCreateCompany() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (values: CompanyFormValues = emptyCompanyForm) => {
      const payload = {
        name: values.name.trim() || "My Company",
        gstin: text(values.gstin)?.toUpperCase() ?? null,
        pan_no: text(values.pan_no)?.toUpperCase() ?? null,
        cin_no: text(values.cin_no)?.toUpperCase() ?? null,
        phone: text(values.phone),
        email: text(values.email),
        website: text(values.website),
        addr1: text(values.addr1),
        addr2: text(values.addr2),
        city: text(values.city),
        state: text(values.state),
        country: values.country.trim() || "India",
        pincode: text(values.pincode),
        address: text(values.addr1),
        ...prefixPayload(values),
        bank_name: text(values.bank_name),
        bank_account: text(values.bank_account),
        bank_ifsc: text(values.bank_ifsc)?.toUpperCase() ?? null,
        updated_by: user?.id ?? null,
      };
      const { data, error } = await supabase.from("company").insert(payload).select("*").single();
      if (error) throw error;
      return normalizeCompany(data as Company);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["company"] });
      toast.success("Company profile created");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to create company"),
  });
}

export function useUpdateDefaults() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, values }: { id: string; values: DefaultsFormValues }) => {
      const payload = {
        default_payment_terms: text(values.default_payment_terms),
        default_gst_slab: values.default_gst_slab,
        fiscal_year_start_month: values.fiscal_year_start_month,
        default_location_id: text(values.default_location_id),
        mill_kerf_mm: values.mill_kerf_mm,
        mill_edge_trim_mm: values.mill_edge_trim_mm,
        mill_weld_allowance_mm: values.mill_weld_allowance_mm,
        mill_tube_weight_factor: values.mill_tube_weight_factor,
        mill_ctl_blade_mm: values.mill_ctl_blade_mm,
        mill_ctl_yield_margin_pct: values.mill_ctl_yield_margin_pct,
        mill_skinpass_weight_factor: values.mill_skinpass_weight_factor,
        mill_inbound_default_process_type_id:
          text(values.mill_inbound_default_process_type_id) || null,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };
      const { data, error } = await supabase
        .from("company")
        .update(payload)
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return normalizeCompany(data as Company);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["company"] });
      toast.success("Defaults saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to save defaults"),
  });
}

export function useTaxRates() {
  return useQuery({
    queryKey: ["tax_rates"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<TaxRate[]> => {
      const { data, error } = await supabase
        .from("tax_rates")
        .select("*")
        .order("tax_type", { ascending: true })
        .order("rate", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((row) => ({
        ...(row as TaxRate),
        rate: money((row as TaxRate).rate),
        gst_slab:
          (row as TaxRate).gst_slab === null || (row as TaxRate).gst_slab === undefined
            ? null
            : money((row as TaxRate).gst_slab),
      }));
    },
  });
}

export function useUpsertTaxRate() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: TaxFormValues }) => {
      const payload = {
        name: values.name.trim(),
        tax_code: values.tax_code.trim().toUpperCase(),
        rate: values.rate,
        tax_type: values.tax_type,
        gst_slab:
          values.gst_slab === null || values.gst_slab === undefined || Number.isNaN(Number(values.gst_slab))
            ? null
            : Number(values.gst_slab),
        effective_from: values.effective_from,
        effective_to: values.effective_to?.trim() || null,
        is_active: values.is_active,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };
      if (id) {
        const { data, error } = await supabase
          .from("tax_rates")
          .update(payload)
          .eq("id", id)
          .select("*")
          .single();
        if (error) throw error;
        return {
          ...(data as TaxRate),
          rate: money((data as TaxRate).rate),
          gst_slab:
            (data as TaxRate).gst_slab === null || (data as TaxRate).gst_slab === undefined
              ? null
              : money((data as TaxRate).gst_slab),
        };
      }
      const { data, error } = await supabase
        .from("tax_rates")
        .insert({ ...payload, created_by: user?.id ?? null })
        .select("*")
        .single();
      if (error) throw error;
      return {
        ...(data as TaxRate),
        rate: money((data as TaxRate).rate),
        gst_slab:
          (data as TaxRate).gst_slab === null || (data as TaxRate).gst_slab === undefined
            ? null
            : money((data as TaxRate).gst_slab),
      };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tax_rates"] });
      toast.success("Tax rate saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to save tax rate"),
  });
}

export function useDeleteTaxRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("tax_rates").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tax_rates"] });
      toast.success("Tax rate deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete tax rate"),
  });
}
